import type {
    VehicleBodyType,
    VehicleCondition,
    VehicleDrive,
    VehicleFeatureGroup,
    VehicleFuel,
    VehicleTransmission,
} from '@/lib/sanity/dto';

/** The route segment the detail pages live under; the listing page carries the same slug. */
export const VEHICLE_BASE_PATH = '/fahrzeuge';

/**
 * The URL parameter naming an inquiry's vehicle by its key: the inquiry page
 * loads the card above the form from it. The readable `?fahrzeug=` beside it
 * fills the form's hidden field.
 */
export const VEHICLE_KEY_PARAM = 'fahrzeug-id';

/** Display form of the condition, e.g. in the technical data. */
export const VEHICLE_CONDITION_LABELS: Record<VehicleCondition, string> = {
    new: 'Neuwagen',
    used: 'Gebrauchtwagen',
    demo: 'Vorführwagen',
    yearOld: 'Jahreswagen',
    dayRegistration: 'Tageszulassung',
};

/** The short form the filter chips use, in the order they appear. */
export const VEHICLE_CONDITION_FILTER_LABELS: Record<VehicleCondition, string> = {
    used: 'Gebraucht',
    new: 'Neu',
    demo: 'Vorführwagen',
    yearOld: 'Jahreswagen',
    dayRegistration: 'Tageszulassung',
};

/** Conditions that describe the offer rather than the car — shown as a photo badge. */
export const VEHICLE_OFFER_CONDITIONS: VehicleCondition[] = ['demo', 'yearOld', 'dayRegistration'];

/** One short label per body type, used by the filter, the subline and the technical data. */
export const VEHICLE_BODY_TYPE_LABELS: Record<VehicleBodyType, string> = {
    offRoad: 'SUV',
    estateCar: 'Kombi',
    limousine: 'Limousine',
    smallCar: 'Kleinwagen',
    van: 'Van',
    sportsCar: 'Sportwagen',
    cabrio: 'Cabrio',
    other: 'Andere',
};

/** Short fuel label for badges and filter chips, in the filter's order. */
export const VEHICLE_FUEL_LABELS: Record<VehicleFuel, string> = {
    electric: 'Elektro',
    petrol: 'Benzin',
    diesel: 'Diesel',
    hybrid: 'Hybrid',
    hybridDiesel: 'Diesel-Hybrid',
    pluginHybrid: 'Plug-in-Hybrid',
    lpg: 'Autogas',
    cng: 'Erdgas',
    hydrogen: 'Wasserstoff',
    ethanol: 'Ethanol',
    biFuel: 'Bi-Fuel',
    other: 'Andere',
};

/** Long fuel label for the technical data. */
export const VEHICLE_FUEL_DETAIL_LABELS: Record<VehicleFuel, string> = {
    ...VEHICLE_FUEL_LABELS,
    hybrid: 'Hybrid (Benzin / Elektro)',
    hybridDiesel: 'Hybrid (Diesel / Elektro)',
    pluginHybrid: 'Plug-in-Hybrid (Benzin / Elektro)',
    lpg: 'Autogas (LPG)',
    cng: 'Erdgas (CNG)',
};

export const VEHICLE_TRANSMISSION_LABELS: Record<VehicleTransmission, string> = {
    automatic: 'Automatik',
    manual: 'Schaltgetriebe',
    semiAutomatic: 'Halbautomatik',
};

export const VEHICLE_DRIVE_LABELS: Record<VehicleDrive, string> = {
    front: 'Frontantrieb',
    rear: 'Heckantrieb',
    allWheel: 'Allradantrieb',
};

/** The equipment groups of the design, in their order. */
export const VEHICLE_FEATURE_GROUP_LABELS: Record<VehicleFeatureGroup, string> = {
    comfort: 'Komfort',
    safety: 'Sicherheit',
    multimedia: 'Multimedia',
};

export const VEHICLE_PRICE_ON_REQUEST = 'Preis auf Anfrage';

/**
 * `sizes` per layout (the design's SIZES). A card row slides at clamp(240px, 78%,
 * 420px) below xl and splits the container from there; the listing grid runs
 * beside the filter column; a list row keeps a fixed photo column.
 */
export const VEHICLE_IMAGE_SIZES = {
    row: '(max-width: 540px) 78vw, (max-width: 1280px) 420px, 340px',
    grid: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1440px) 30vw, 380px',
    list: '(max-width: 640px) 100vw, 220px',
    /** The full-screen viewer's too: it shows the file the page has loaded already. */
    gallery: '(max-width: 1024px) 100vw, (max-width: 1440px) 62vw, 900px',
    /** 17% of the strip, 84–140px; the viewer's strip is 100px throughout. */
    thumb: '(max-width: 640px) 104px, 140px',
} as const;
